import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { ToWords } from 'to-words';
import { FinanceService } from 'src/app/services/finance.service';
import { MaterialModule } from 'src/app/shared/material.module';

pdfMake.vfs = pdfFonts.vfs;
const toWords = new ToWords();

@Component({
  selector: 'app-credit-note-print',
  standalone: true,
  imports: [CommonModule, MaterialModule, MatProgressBar],
  templateUrl: './credit-note-print.component.html',
  styleUrl: './credit-note-print.component.scss'
})
export class CreditNotePrintComponent implements OnInit, OnDestroy {
  creditNoteId!: number;
  creditNoteData: any;
  previewData: any = null;
  pdfDoc: any;
  isDialogMode = false;
  includeSignature = false;
  private originalHtmlOverflow = '';
  private originalBodyOverflow = '';

  constructor(
    private route: ActivatedRoute,
    private financeService: FinanceService,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<CreditNotePrintComponent> | null
  ) {}

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.originalHtmlOverflow = document.documentElement.style.overflow;
    this.originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.isDialogMode = !!this.dialogData?.id;
    this.creditNoteId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (this.creditNoteId) {
      this.loadCreditNoteData();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    document.documentElement.style.overflow = this.originalHtmlOverflow;
    document.body.style.overflow = this.originalBodyOverflow;
  }

  printPdf() {
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.pdfDoc?.download(`Credit-Note-${this.previewData?.voucherNo || this.creditNoteId}.pdf`);
  }

  sendMail() {
    this.snackBar.open('Credit note mail template configured nahi mila. Mail compose open nahi kiya gaya.', 'Close', {
      duration: 4000
    });
  }

  onSignatureToggle(checked: boolean) {
    if (this.includeSignature === checked) {
      return;
    }

    this.includeSignature = checked;
    this.generatePdf();
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private loadCreditNoteData(): void {
    this.financeService.getCreditNotePrintById(this.creditNoteId).subscribe({
      next: (data: any) => {
        this.creditNoteData = Array.isArray(data) ? data[0] : data;
        this.previewData = this.buildPreviewData(this.creditNoteData || {});
        this.generatePdf();
      },
      error: (err) => console.error('Error loading credit note print', err)
    });
  }

  private buildPreviewData(source: any) {
    const company = this.pickEntity(source, ['company', 'companymaster', 'companydetails']);
    const branch = this.pickEntity(source, ['branch']);
    const client = this.pickEntity(source, ['client', 'party', 'customerdetails']);
    const header = source.header || source;
    const rawInvoices = this.asArray(source.vouchersubdetails);

    const invoiceRows = rawInvoices.map((invoice: any, index: number) => {
      const items = this.asArray(invoice.vouchersubdetail_items).map((item: any, itemIndex: number) => {
        const taxes = this.asArray(item.vouchersubdetailitemtaxdetail).map((tax: any) => ({
          taxName: this.safeValue(tax.taxname, '-'),
          taxRate: Number(tax.taxrate || 0),
          taxableAmount: this.formatCurrency(tax.taxableamount || 0, true),
          taxAmount: this.formatCurrency(tax.taxamount || 0, true)
        }));

        return {
          sr: itemIndex + 1,
          productName: this.safeValue(item.productname, '-'),
          qty: this.formatNumber(item.qty || 0),
          rate: this.formatCurrency(item.rate || 0, true),
          grossAmount: this.formatCurrency(item.grossamt || 0, true),
          taxAmount: this.formatCurrency(item.taxamt || 0, true),
          netAmount: this.formatCurrency(item.netamt || 0, true),
          remarks: this.safeValue(item.remarks, '-'),
          hasTaxes: taxes.length > 0,
          taxes,
          taxSummary: taxes.map((tax) => `${tax.taxName} (${this.formatNumber(tax.taxRate)}%)`).join(', ')
        };
      });

      const totalTax = items.reduce((sum: number, item: any) => sum + this.parseCurrency(item.taxAmount), 0);
      const totalNet = items.reduce((sum: number, item: any) => sum + this.parseCurrency(item.netAmount), 0);
      const totalGross = items.reduce((sum: number, item: any) => sum + this.parseCurrency(item.grossAmount), 0);

      return {
        sr: index + 1,
        billNo: this.safeValue(invoice.billno, '-'),
        payableAmount: this.formatCurrency(invoice.payableamt || 0, true),
        creditAmount: this.formatCurrency(invoice.dbcramount || 0, true),
        roundOffAmount: this.formatCurrency(invoice.roundoffamount || 0, true),
        items,
        totalTax: this.formatCurrency(totalTax, true),
        totalNet: this.formatCurrency(totalNet, true),
        totalGross: this.formatCurrency(totalGross, true)
      };
    });

    const totalCredit = Number(header.totalcredit ?? invoiceRows.reduce((sum: number, row: any) => sum + this.parseCurrency(row.creditAmount), 0));
    const totalDebit = Number(header.totaldebit ?? totalCredit);

    return {
      logo: this.resolveImage([
        company.logoBase64,
        company.logobase64,
        company.logo,
        source.logoBase64,
        source.logobase64
      ]),
      signatureImage: this.resolveImage([
        company.signature,
        company.signatureBase64,
        company.signaturebase64,
        source.signature,
        source.signatureBase64
      ]),
      stampImage: this.resolveImage([
        company.stamp,
        company.stampBase64,
        company.stampbase64,
        source.stamp,
        source.stampBase64
      ]),
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name, 'VCS ERP'),
      companyNameTitle: this.safeValue(company.name, 'VCS ERP'),
      companyAddress: this.joinAddress([company.address1, company.address2, company.city, company.state, company.country, company.pincode]),
      companyMeta: this.buildCompanyMeta(company),
      signatoryName: this.safeValue(
        company.authorized_signatory_name ||
        company.authorizedSignatoryName ||
        source.authorized_signatory_name ||
        source.authorizedSignatoryName,
        ''
      ),
      signatoryTitle: this.safeValue(
        company.authorized_signatory_title ||
        company.authorizedSignatoryTitle ||
        source.authorized_signatory_title ||
        source.authorizedSignatoryTitle,
        'Authorized Signatory'
      ),
      voucherNo: this.safeValue(header.voucherno || header.code),
      voucherDate: this.formatDateValue(header.voucherdate),
      referenceNo: this.safeValue(header.partyrefinvoiceno, '-'),
      referenceDate: this.formatDateValue(header.partyrefinvoicedate),
      branchName: this.safeValue(branch.branchname, '-'),
      clientName: this.safeValue(client.name || client.clientname, '-'),
      clientAddress: this.joinAddress([
        client.address1,
        client.address2,
        client.city,
        client.state,
        client.country,
        client.pincode
      ]),
      clientMeta: this.buildPartyMeta(client),
      remarks: this.safeValue(header.remarks, '-'),
      overview: [
        { label: 'Client', value: this.safeValue(client.name || client.clientname, '-') },
        { label: 'Invoices', value: String(invoiceRows.length) },
        { label: 'Total Credit', value: this.formatCurrency(totalCredit, true) },
        { label: 'Round Off', value: this.formatCurrency(header.roundoffamount || 0, true) }
      ],
      hasInvoiceRows: invoiceRows.length > 0,
      invoiceRows,
      totalCredit: this.formatCurrency(totalCredit, true),
      totalDebit: this.formatCurrency(totalDebit, true),
      amountInWords: `${toWords.convert(totalCredit, { currency: true })}/-`
    };
  }

  private generatePdf() {
    if (!this.previewData) {
      return;
    }

    const content: any[] = [
      {
        canvas: [
          { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
          { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
          { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
        ],
        margin: [0, -10, 0, 4]
      },
      {
        table: {
          widths: [74, '*', 168],
          body: [[
            {
              stack: this.previewData.logo
                ? [{ image: this.previewData.logo, width: 66, fit: [66, 66], alignment: 'center' }]
                : [{ text: this.previewData.companyInitials, style: 'logoFallback' }],
              border: [false, false, false, false],
              margin: [0, 6, 0, 0]
            },
            {
              stack: [
                { text: this.previewData.companyName.toUpperCase(), style: 'companyHeader' },
                this.previewData.companyAddress ? { text: this.previewData.companyAddress, style: 'companyMeta' } : '',
                ...this.previewData.companyMeta.map((text: string) => ({ text, style: 'companyMeta' }))
              ].filter(Boolean),
              border: [false, false, false, false],
              margin: [0, 4, 0, 0]
            },
            {
              stack: [
                { text: 'CREDIT NOTE', style: 'invoiceHeaderBadge' },
                { text: `Voucher No: ${this.previewData.voucherNo}`, style: 'invoiceMetaStrong' },
                { text: `Voucher Date: ${this.previewData.voucherDate}`, style: 'invoiceMeta' },
                { text: `Reference No: ${this.previewData.referenceNo}`, style: 'invoiceMeta' },
                { text: `Reference Date: ${this.previewData.referenceDate}`, style: 'invoiceMeta' }
              ],
              fillColor: '#f6f8ff',
              margin: [10, 8, 10, 8],
              border: [true, true, true, true]
            }
          ]]
        },
        layout: {
          hLineColor: () => '#d7deea',
          vLineColor: () => '#d7deea'
        }
      },
      {
        columns: this.previewData.overview.map((card: any) => ({
          width: '*',
          stack: [
            { text: card.label, style: 'cardTitle' },
            { text: card.value, style: 'cardHeading' }
          ],
          margin: [8, 8, 8, 8],
          fillColor: '#f8fafc'
        })),
        columnGap: 12,
        margin: [0, 10, 0, 8]
      },
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: 'Client Details', style: 'sectionHeader' },
              { text: this.previewData.clientName, style: 'cardHeading' },
              ...(this.previewData.clientAddress ? [{ text: this.previewData.clientAddress, style: 'cardText' }] : []),
              ...this.previewData.clientMeta.map((text: string) => ({ text, style: 'cardText' }))
            ],
            margin: [8, 8, 8, 8],
            fillColor: '#f8fafc'
          },
          {
            width: 188,
            stack: [
              { text: 'Branch', style: 'sectionHeader' },
              { text: this.previewData.branchName, style: 'cardHeading' }
            ],
            margin: [8, 8, 8, 8],
            fillColor: '#f8fafc'
          }
        ],
        columnGap: 12,
        margin: [0, 0, 0, 8]
      }
    ];

    this.previewData.invoiceRows.forEach((invoice: any) => {
      const itemBody = [
        [
          { text: 'SR', style: 'tableHeader' },
          { text: 'ITEM', style: 'tableHeader' },
          { text: 'QTY', style: 'tableHeader' },
          { text: 'RATE', style: 'tableHeader' },
          { text: 'GROSS', style: 'tableHeader' },
          { text: 'TAX', style: 'tableHeader' },
          { text: 'NET', style: 'tableHeader' }
        ],
        ...invoice.items.flatMap((item: any) => {
          const rows: any[] = [[
            { text: String(item.sr), alignment: 'center' },
            {
              stack: [
                { text: item.productName },
                ...(item.taxSummary ? [{ text: item.taxSummary, fontSize: 8, color: '#64748b' }] : []),
                ...(item.remarks !== '-' ? [{ text: `Remarks: ${item.remarks}`, fontSize: 8, color: '#64748b' }] : [])
              ]
            },
            { text: item.qty, alignment: 'right' },
            { text: item.rate, alignment: 'right' },
            { text: item.grossAmount, alignment: 'right' },
            { text: item.taxAmount, alignment: 'right' },
            { text: item.netAmount, alignment: 'right', bold: true }
          ]];

          if (item.hasTaxes) {
            rows.push([
              { text: '', border: [true, false, true, true] },
              {
                colSpan: 6,
                border: [true, false, true, true],
                stack: [
                  { text: 'Tax Breakup', fontSize: 8.4, bold: true, color: '#0b7285', margin: [0, 2, 0, 3] },
                  ...item.taxes.map((tax: any) => ({
                    text: `${tax.taxName} (${this.formatNumber(tax.taxRate)}%) | Taxable ${tax.taxableAmount} | Tax ${tax.taxAmount}`,
                    fontSize: 8,
                    color: '#475569'
                  }))
                ]
              },
              {}, {}, {}, {}, {}
            ]);
          }

          return rows;
        }),
        [
          { text: 'INVOICE TOTAL', colSpan: 4, alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 4, 8, 4] },
          {}, {}, {},
          { text: invoice.totalGross, alignment: 'right', bold: true, fillColor: '#eef2ff' },
          { text: invoice.totalTax, alignment: 'right', bold: true, fillColor: '#eef2ff' },
          { text: invoice.totalNet, alignment: 'right', bold: true, fillColor: '#eef2ff' }
        ]
      ];

      content.push(
        {
          margin: [0, 10, 0, 0],
          table: {
            widths: ['*'],
            body: [[{
              stack: [
                { text: `Invoice #: ${invoice.billNo}`, style: 'sectionHeader' },
                {
                  columns: [
                    { width: '*', text: `Pending ${invoice.payableAmount}`, style: 'cardText' },
                    { width: 140, text: `Credit ${invoice.creditAmount}`, style: 'cardText', alignment: 'right' },
                    { width: 140, text: `Round Off ${invoice.roundOffAmount}`, style: 'cardText', alignment: 'right' }
                  ]
                }
              ]
            }]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea',
            hLineWidth: () => 0.7,
            vLineWidth: () => 0.7
          }
        },
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: [24, '*', 42, 56, 60, 54, 60],
            body: itemBody
          },
          layout: this.buildTableLayout()
        }
      );
    });

    content.push({
      margin: [0, 10, 0, 0],
      columns: [
        {
          width: '*',
          stack: [
            { text: 'Amount In Words', style: 'sectionHeader' },
            { text: this.previewData.amountInWords, style: 'cardText' },
            ...(this.previewData.remarks !== '-' ? [
              { text: 'Remarks', style: 'sectionHeader', margin: [0, 12, 0, 0] },
              { text: this.previewData.remarks, style: 'cardText' }
            ] : [])
          ]
        },
        {
          width: 188,
          unbreakable: true,
          stack: [
            {
              margin: [0, 50, 0, 0],
              unbreakable: true,
              stack: [
                { text: `For ${this.previewData.companyNameTitle}`, alignment: 'right', bold: true, color: '#153a6b' },
                ...(this.includeSignature
                  ? [{
                      columns: [
                        { width: '*', text: '' },
                        {
                          width: 'auto',
                          stack: [
                            {
                              columns: [
                                ...(this.previewData.stampImage ? [{ width: 70, image: this.previewData.stampImage, fit: [70, 70], margin: [0, 4, 6, 0] }] : []),
                                ...(this.previewData.signatureImage ? [{ width: 74, image: this.previewData.signatureImage, fit: [74, 34], margin: [0, 22, 0, 0] }] : [])
                              ]
                            }
                          ]
                        }
                      ],
                      margin: [0, 8, 0, 0]
                    }]
                  : []),
                { text: this.previewData.signatoryName || '', alignment: 'right', margin: [0, 12, 0, 0], fontSize: 8.6, bold: true },
                { text: this.previewData.signatoryTitle || 'Authorized Signatory', alignment: 'right', fontSize: 8.6, color: '#475569' }
              ]
            }
          ]
        }
      ]
    });

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 12, 20, 42],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              { width: '*', text: 'Generated using VCS ERP', style: 'footerText' },
              { width: 90, text: `Page ${currentPage} of ${pageCount}`, style: 'footerPageText', alignment: 'right' }
            ]
          }
        ]
      }),
      background: this.previewData.logo
        ? { image: this.previewData.logo, alignment: 'center', width: 300, height: 300, opacity: 0.05, marginTop: 220 }
        : undefined,
      content,
      styles: {
        companyHeader: { fontSize: 18, bold: true, color: '#0f2852', alignment: 'center' },
        companyMeta: { fontSize: 10.2, color: '#2b3e5f', alignment: 'center', margin: [0, 1, 0, 0] },
        invoiceHeaderBadge: { fontSize: 11, bold: true, color: '#0f2852', margin: [0, 0, 0, 8] },
        invoiceMetaStrong: { fontSize: 9.4, bold: true, color: '#12253f', margin: [0, 2, 0, 0] },
        invoiceMeta: { fontSize: 9, color: '#334155', margin: [0, 2, 0, 0] },
        cardTitle: { fontSize: 9, bold: true, color: '#48627f', margin: [0, 0, 0, 6] },
        cardHeading: { fontSize: 12, bold: true, color: '#0f2852' },
        cardText: { fontSize: 9.1, color: '#334155', margin: [0, 2, 0, 0] },
        tableHeader: { fontSize: 8.7, bold: true, color: '#ffffff', fillColor: '#17354d', alignment: 'center', margin: [0, 4, 0, 4] },
        sectionHeader: { fontSize: 11.2, bold: true, color: '#0b7285', margin: [0, 0, 0, 6] },
        footerText: { fontSize: 8, color: '#64748b' },
        footerPageText: { fontSize: 8, color: '#334155', bold: true },
        logoFallback: {
          fontSize: 26,
          bold: true,
          color: '#153a6b',
          alignment: 'center',
          margin: [0, 16, 0, 0]
        }
      },
      defaultStyle: {
        fontSize: 9,
        color: '#0f172a'
      }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
  }

  private buildTableLayout() {
    return {
      hLineColor: () => '#b9c7d8',
      vLineColor: () => '#b9c7d8',
      hLineWidth: () => 0.7,
      vLineWidth: () => 0.7,
      fillColor: (rowIndex: number) => rowIndex === 0 ? '#17354d' : rowIndex % 2 === 0 ? '#f8fafc' : null
    };
  }

  private pickEntity(source: any, keys: string[]) {
    for (const key of keys) {
      if (source?.[key]) {
        return source[key];
      }
    }
    return {};
  }

  private buildCompanyMeta(company: any): string[] {
    const gstLine = this.metaLine('GSTIN', company.gstin || company.gstnumber, '');
    const panLine = this.metaLine('PAN', company.pan || company.panno, '');
    const emailLine = this.metaLine('Email', company.email || company.emailid, '');
    const websiteLine = this.metaLine('Website', company.website, '');

    return [
      [gstLine, panLine].filter(Boolean).join('    '),
      [emailLine, websiteLine].filter(Boolean).join('    ')
    ].filter(Boolean);
  }

  private buildPartyMeta(party: any): string[] {
    const gstLine = this.metaLine('GSTIN', party.gstin || party.gstnumber, '');
    const panLine = this.metaLine('PAN', party.pan || party.panno, '');
    const phoneLine = this.metaLine('Phone', party.phone || party.phoneno || party.mobile || party.mobileno, '');
    const emailLine = this.metaLine('Email', party.email || party.emailid, '');

    return [gstLine, panLine, phoneLine, emailLine].filter(Boolean);
  }

  private metaLine(label: string, value: any, fallback = ''): string {
    const cleaned = this.safeValue(value, '');
    return cleaned ? `${label}: ${cleaned}` : fallback;
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value, '-');
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private formatCurrency(value: any, showSymbol = true): string {
    const amount = Number(value || 0);
    const formatter = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return showSymbol ? `₹ ${formatter.format(amount)}` : formatter.format(amount);
  }

  private formatNumber(value: any): string {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(Number(value || 0));
  }

  private parseCurrency(value: string): number {
    return Number(String(value || '0').replace(/[^\d.-]/g, '')) || 0;
  }

  private joinAddress(parts: any[]): string {
    return parts
      .map((part) => this.safeValue(part, ''))
      .filter(Boolean)
      .join(', ');
  }

  private getInitials(name: string): string {
    return this.safeValue(name, 'VC')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('');
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'string') {
        continue;
      }

      const trimmed = candidate.trim();
      if (!trimmed) {
        continue;
      }

      if (trimmed.startsWith('data:image')) {
        return trimmed;
      }

      return `data:image/png;base64,${trimmed}`;
    }

    return null;
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined) {
      return fallback;
    }

    const text = String(value).trim();
    return text ? text : fallback;
  }

  private asArray(value: any): any[] {
    return Array.isArray(value) ? value : [];
  }
}
